import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ByoSchemaField } from './types';

export interface SamplePreviewProps {
  /** Inferred schema fields (name + type). */
  schema?: ByoSchemaField[] | null;
  /** Sample rows (array of records). */
  rows?: Array<Record<string, unknown>> | null;
  /** Title above the preview. */
  title?: string;
  /** Max rows to render. Default 20. */
  maxRows?: number;
  'data-testid'?: string;
}

/**
 * Presentational sample/output preview: a schema-chip strip + a record table.
 * Shared by InputPanel (source sample) and TestPanel (post-processor output).
 */
export function SamplePreview({
  schema,
  rows,
  title,
  maxRows = 20,
  'data-testid': dataTestId = 'byo-sample-preview',
}: SamplePreviewProps) {
  const columns = useMemo(() => {
    if (schema && schema.length) return schema.map((f) => f.name);
    if (rows && rows.length) return Object.keys(rows[0]);
    return [];
  }, [schema, rows]);

  const visibleRows = useMemo(() => (rows ?? []).slice(0, maxRows), [rows, maxRows]);

  if (!schema?.length && !rows?.length) {
    return (
      <Box data-testid={`${dataTestId}-empty`}>
        <Typography variant="body2" color="textSecondary">
          No sample yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid={dataTestId}>
      {title && (
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
      )}

      {schema?.length ? (
        <Box
          data-testid={`${dataTestId}-schema`}
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}
        >
          {schema.map((field) => (
            <Chip
              key={field.name}
              size="small"
              label={`${field.name}: ${field.type}`}
              data-testid={`${dataTestId}-schema-field`}
            />
          ))}
        </Box>
      ) : null}

      {columns.length ? (
        <TableContainer
          sx={{
            maxHeight: 260,
            overflow: 'auto',
            border: '1px solid var(--color-greyscale-200, #e6e7eb)',
            borderRadius: '8px',
          }}
        >
          {/* No stickyHeader: it escapes the scroll container and overlaps the
              page header in some shells. A bounded scroll box is enough. */}
          <Table size="small" data-testid={`${dataTestId}-table`}>
            <TableHead>
              <TableRow
                sx={{
                  '& th': { background: 'var(--color-greyscale-50, #f6f6f3)', fontWeight: 600 },
                }}
              >
                {columns.map((col) => (
                  <TableCell key={col}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row, idx) => (
                <TableRow key={idx} data-testid={`${dataTestId}-row`}>
                  {columns.map((col) => (
                    <TableCell key={col}>{formatCell(row[col])}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
}

function formatCell(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default SamplePreview;
